import { DateTime } from 'luxon';
import { useExternalParams } from 'shared/hooks';
import { useEffect, useMemo, useRef } from 'react';
import { ProviderFactory } from 'shared/providers/provider-factory';
import { PlanningParamsContext } from './planning-params-provider.types';

const initialValues = {
  branchOffices: {
    initialValue: undefined,
    type: 'string_array',
  },
  departments: {
    initialValue: undefined,
    type: 'string_array',
  },
  shiftId: {
    initialValue: undefined,
    type: 'string',
  },
  date: {
    initialValue: undefined,
    type: 'date',
  },
  fromDate: {
    initialValue: undefined,
    type: 'date',
  },
  toDate: {
    initialValue: undefined,
    type: 'date',
  },
  filledStatus: {
    initialValue: undefined,
    type: 'string',
  },
  hasApplicants: {
    initialValue: undefined,
    type: 'boolean',
  },
  jobType: {
    initialValue: undefined,
    type: 'string_array',
  },
  isFreelance: {
    initialValue: undefined,
    type: 'boolean',
  },
  isTempWork: {
    initialValue: undefined,
    type: 'boolean',
  },
  isEmployee: {
    initialValue: undefined,
    type: 'boolean',
  },
} as const;

const [Provider, usePlanningParamsProvider] = ProviderFactory<PlanningParamsContext>(
  'PlanningParamsContext can only be accessed within PlanningProvider'
);

type PlanningParamsProviderProps = {
  children: React.ReactNode;
};

export const PlanningParamsProvider = ({ children }: PlanningParamsProviderProps) => {
  const [params, setParams] = useExternalParams(initialValues);
  const savedParamsRef = useRef(params);

  useEffect(() => {
    savedParamsRef.current = {
      ...savedParamsRef.current,
      departments: params.departments,
      shiftId: params.shiftId,
      isTempWork: params.isTempWork,
      isFreelance: params.isFreelance,
      isEmployee: params.isEmployee,
      branchOffices: params.branchOffices,
      jobType: params.jobType,
      hasApplicants: params.hasApplicants,
      filledStatus: params.filledStatus,
    };
  }, [
    params.departments,
    params.shiftId,
    params.isTempWork,
    params.isFreelance,
    params.isEmployee,
    params.branchOffices,
    params.jobType,
    params.hasApplicants,
    params.filledStatus,
  ]);

  const filters = useMemo(() => {
    return {
      branchOfficeIds: params.branchOffices as string[] | undefined,
      departmentIds: params.departments as string[] | undefined,
      shiftId: params.shiftId,
      date: params.date,
      fromDate: params.fromDate,
      toDate: params.toDate,
      filledStatus: params.filledStatus || undefined,
      hasApplicants: params.hasApplicants ?? undefined,
      jobType: params.jobType as string[] | undefined,
      isFreelance: params.isFreelance ? params.isFreelance : undefined,
      isTempWork: params.isTempWork ? params.isTempWork : undefined,
      isEmployee: params.isEmployee ? params.isEmployee : undefined,
    };
  }, [params]);
  const selectedFiltersCount = Object.values(filters).filter(
    (value) => value !== null && value !== undefined && value !== '' && !isDateTime(value)
  ).length;
  return (
    <Provider
      value={{
        filters,
        savedParamsRef,
        setFilters: setParams,
        selectedFiltersCount,
      }}
    >
      {children}
    </Provider>
  );
};

export const usePlanningParamsContext = () => {
  const planningContext = usePlanningParamsProvider();
  return planningContext;
};

const isDateTime = (filterValue: any) => {
  return filterValue instanceof DateTime;
};
